import { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { api } from "../lib/api";
import { Modal, PrimaryButton } from "./common";
import { toast } from "sonner";
import { PencilSimple, TextT, ArrowCounterClockwise, Trash } from "@phosphor-icons/react";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

const COLORS = ["#EF4444", "#FF5A00", "#FACC15", "#22C55E", "#3B82F6", "#111827", "#FFFFFF"];

export function AnnotationEditor({ open, onClose, storagePath, onSave, saving }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const drawing = useRef(false);
  const current = useRef(null);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#EF4444");
  const [size, setSize] = useState(4);
  const [actions, setActions] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open || !storagePath) return;
    setReady(false); setActions([]);
    let url;
    const setup = (w, h) => {
      const cv = canvasRef.current;
      if (!cv) return;
      const maxW = 1400;
      const scale = w > maxW ? maxW / w : 1;
      cv.width = Math.round(w * scale);
      cv.height = Math.round(h * scale);
      setReady(true);
      redraw([]);
    };
    api.get(`/files/${storagePath}`, { responseType: "blob" }).then(async (r) => {
      const blob = r.data;
      const isPdf = blob.type === "application/pdf" || /\.pdf$/i.test(storagePath);
      if (isPdf) {
        try {
          const buf = await blob.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 2 });
          const tmp = document.createElement("canvas");
          tmp.width = viewport.width; tmp.height = viewport.height;
          await page.render({ canvasContext: tmp.getContext("2d"), viewport }).promise;
          imgRef.current = tmp;
          setup(tmp.width, tmp.height);
        } catch (e) { toast.error("No se pudo renderizar el PDF para anotar"); }
      } else {
        url = URL.createObjectURL(blob);
        const im = new Image();
        im.onload = () => { imgRef.current = im; setup(im.width, im.height); };
        im.src = url;
      }
    }).catch(() => toast.error("No se pudo cargar el archivo para anotar"));
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storagePath]);

  const redraw = (acts) => {
    const cv = canvasRef.current; if (!cv || !imgRef.current) return;
    const ctx = cv.getContext("2d");
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(imgRef.current, 0, 0, cv.width, cv.height);
    (acts || actions).forEach((a) => {
      if (a.type === "path") {
        ctx.strokeStyle = a.color; ctx.lineWidth = a.size; ctx.lineJoin = "round"; ctx.lineCap = "round";
        ctx.beginPath();
        a.points.forEach((p, i) => { if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y); });
        ctx.stroke();
      } else if (a.type === "text") {
        ctx.fillStyle = a.color; ctx.font = `bold ${a.size * 6}px sans-serif`; ctx.textBaseline = "top";
        ctx.fillText(a.text, a.x, a.y);
      }
    });
  };

  const pos = (e) => {
    const cv = canvasRef.current; const rect = cv.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * (cv.width / rect.width), y: (e.clientY - rect.top) * (cv.height / rect.height) };
  };

  const down = (e) => {
    if (!ready) return;
    e.preventDefault();
    const p = pos(e);
    if (tool === "text") {
      const t = window.prompt("Escribe la nota:");
      if (t) { const next = [...actions, { type: "text", x: p.x, y: p.y, text: t, color, size }]; setActions(next); redraw(next); }
      return;
    }
    drawing.current = true;
    current.current = { type: "path", color, size, points: [p] };
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    current.current.points.push(pos(e));
    redraw([...actions, current.current]);
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (current.current) { setActions([...actions, current.current]); current.current = null; }
  };

  const undo = () => { const next = actions.slice(0, -1); setActions(next); redraw(next); };
  const clearAll = () => { setActions([]); redraw([]); };
  const save = () => {
    const cv = canvasRef.current; if (!cv) return;
    cv.toBlob((blob) => {
      if (!blob) return toast.error("No se pudo generar la imagen anotada");
      onSave(new File([blob], `anotacion_${Date.now()}.png`, { type: "image/png" }));
    }, "image/png");
  };

  return (
    <Modal open={open} onClose={onClose} title="Anotar imagen">
      <div className="space-y-3" data-testid="annotation-editor">
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => setTool("pen")} data-testid="tool-pen" className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-sm border ${tool === "pen" ? "bg-primary text-white border-primary" : "border-input"}`}><PencilSimple size={16} /> Dibujar</button>
          <button type="button" onClick={() => setTool("text")} data-testid="tool-text" className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm text-sm border ${tool === "text" ? "bg-primary text-white border-primary" : "border-input"}`}><TextT size={16} /> Texto</button>
          <div className="flex items-center gap-1 ml-1">
            {COLORS.map((c) => <button key={c} type="button" onClick={() => setColor(c)} className={`w-6 h-6 rounded-full border-2 transition-transform duration-150 ${color === c ? "border-foreground scale-110" : "border-slate-200"}`} style={{ background: c }} />)}
          </div>
          <input type="range" min="2" max="10" value={size} onChange={(e) => setSize(Number(e.target.value))} className="w-20" />
          <button type="button" onClick={undo} data-testid="tool-undo" className="inline-flex items-center gap-1 px-2 py-1.5 rounded-sm text-sm border border-input"><ArrowCounterClockwise size={16} /> Deshacer</button>
          <button type="button" onClick={clearAll} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-sm text-sm border border-input"><Trash size={16} /> Limpiar</button>
        </div>
        <div className="border border-border rounded-sm overflow-auto bg-muted max-h-[60vh]">
          <canvas ref={canvasRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
            className="max-w-full block touch-none" style={{ cursor: "crosshair" }} />
        </div>
        <p className="text-xs text-muted-foreground">Dibuja o agrega notas de texto sobre la imagen. Al guardar se crea una copia anotada.</p>
        <PrimaryButton onClick={save} disabled={!ready || saving} data-testid="save-annotation-btn">{saving ? "Guardando…" : "Guardar anotación"}</PrimaryButton>
      </div>
    </Modal>
  );
}
